<template>
  <div class="current-set-score">
    <div class="score-row">
      <div class="team-cell">
        <span v-if="startPoint === 'Serve'" class="marker">•</span>
        <span class="team-name">{{ safeTeamName }}</span>
      </div>
      <div class="sets-cell">{{ setsWon }}</div>
      <div
        v-for="(cell, idx) in teamSetCells"
        :key="`team-set-${idx}`"
        class="set-cell"
        :class="{
          'finished-cell': cell.finished,
          'won-set-cell': cell.wonSet,
          'active-set-cell': cell.activeSet,
          'last-scored': cell.activeSet && lastScoredSide === 'team',
          'last-scored-team': cell.activeSet && lastScoredSide === 'team',
          'scored-flash': cell.activeSet && flashSide === 'team',
          'scored-flash-team': cell.activeSet && flashSide === 'team'
        }"
      >
        {{ cell.value }}
      </div>
    </div>
    <div class="score-row">
      <div class="team-cell">
        <span v-if="startPoint === 'Receive'" class="marker">•</span>
        <span class="team-name">{{ safeOppositionName }}</span>
      </div>
      <div class="sets-cell">{{ setsLost }}</div>
      <div
        v-for="(cell, idx) in oppositionSetCells"
        :key="`opp-set-${idx}`"
        class="set-cell"
        :class="{
          'finished-cell': cell.finished,
          'won-set-cell': cell.wonSet,
          'active-set-cell': cell.activeSet,
          'last-scored': cell.activeSet && lastScoredSide === 'opposition',
          'last-scored-opposition': cell.activeSet && lastScoredSide === 'opposition',
          'scored-flash': cell.activeSet && flashSide === 'opposition',
          'scored-flash-opposition': cell.activeSet && flashSide === 'opposition'
        }"
      >
        {{ cell.value }}
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';

export default {
  name: 'CurrentSetScore',
  data() {
    return {
      flashSide: null,
      lastScoredSide: null,
      flashTimer: null
    };
  },
  props: {
    score: {
      type: Object,
      required: true,
      default: () => ({ won: 0, lost: 0 })
    },
    setsWon: {
      type: Number,
      default: 0
    },
    setsLost: {
      type: Number,
      default: 0
    },
    teamName: {
      type: String,
      default: 'Moss Side Jaguars'
    },
    oppositionName: {
      type: String,
      default: 'Opposition'
    },
    startPoint: {
      type: String,
      required: true
    },
  },
  computed: {
    safeTeamName() {
      return this.teamName || 'Moss Side Jaguars';
    },
    safeOppositionName() {
      return this.oppositionName || 'Opposition';
    },
    setScores() {
      const sets = Array.isArray(store.match?.sets) ? store.match.sets : [];
      const currentSetIndex = Number.isInteger(store.match?.currentSetIndex) ? store.match.currentSetIndex : 0;

      const team = sets.map(set => (set?.score && typeof set.score.won === 'number') ? set.score.won : 0);
      const opposition = sets.map(set => (set?.score && typeof set.score.lost === 'number') ? set.score.lost : 0);

      if (currentSetIndex >= 0) {
        while (team.length <= currentSetIndex) {
          team.push(0);
          opposition.push(0);
        }
        team[currentSetIndex] = this.score?.won ?? team[currentSetIndex];
        opposition[currentSetIndex] = this.score?.lost ?? opposition[currentSetIndex];
      }

      return { team, opposition };
    },
    setStatus() {
      const sets = Array.isArray(store.match?.sets) ? store.match.sets : [];
      const currentSetIndex = Number.isInteger(store.match?.currentSetIndex) ? store.match.currentSetIndex : 0;
      return this.setScores.team.map((_, idx) => {
        const set = sets[idx];
        const setScore = set?.score || {};
        const finished = Boolean(set?.finished) || idx < currentSetIndex;
        const teamWonSet = finished && typeof setScore.won === 'number' && typeof setScore.lost === 'number'
          ? setScore.won > setScore.lost
          : false;
        const oppositionWonSet = finished && typeof setScore.won === 'number' && typeof setScore.lost === 'number'
          ? setScore.lost > setScore.won
          : false;
        const matchFinished = Boolean(store.match?.finished);
        return {
          finished,
          teamWonSet,
          oppositionWonSet,
          activeSet: !matchFinished && idx === currentSetIndex
        };
      });
    },
    hasFinishedSets() {
      return this.setStatus.some(status => status.finished);
    },
    teamSetCells() {
      return this.setScores.team.map((value, idx) => {
        const status = this.setStatus[idx] || {};
        return {
          value,
          finished: Boolean(status.finished),
          wonSet: Boolean(status.teamWonSet),
          activeSet: Boolean(status.activeSet)
        };
      });
    },
    oppositionSetCells() {
      return this.setScores.opposition.map((value, idx) => {
        const status = this.setStatus[idx] || {};
        return {
          value,
          finished: Boolean(status.finished),
          wonSet: Boolean(status.oppositionWonSet),
          activeSet: Boolean(status.activeSet)
        };
      });
    }
  },
  watch: {
    'score.won'(next, prev) {
      if (typeof prev !== 'number') {
        return;
      }
      if (Number(next) > Number(prev)) {
        this.triggerScoreFlash('team');
      }
    },
    'score.lost'(next, prev) {
      if (typeof prev !== 'number') {
        return;
      }
      if (Number(next) > Number(prev)) {
        this.triggerScoreFlash('opposition');
      }
    }
  },
  methods: {
    triggerScoreFlash(side) {
      this.lastScoredSide = side;
      if (this.flashTimer) {
        clearTimeout(this.flashTimer);
      }
      // Toggle flash class off/on to retrigger animation reliably.
      this.flashSide = null;
      this.$nextTick(() => {
        this.flashSide = side;
        this.flashTimer = setTimeout(() => {
          this.flashSide = null;
          this.flashTimer = null;
        }, 1000);
      });
    }
  },
  beforeUnmount() {
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
      this.flashTimer = null;
    }
  }
};
</script>

<style scoped>
.current-set-score {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  border-radius: 8px;
  overflow: hidden;
}

.score-row {
  display: flex;
  gap: 1px;
}

.team-cell,
.sets-cell,
.set-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  height: 30px;
  box-sizing: border-box;
}

.team-cell {
  justify-content: flex-start;
  gap: 8px;
  background: var(--grey-35);
  color: var(--grey-900);
  padding: 0 16px;
  width: clamp(120px, 24vw, 240px);
  min-width: 120px;
  max-width: 240px;
}

.team-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
}

.sets-cell {
  width: 30px;
  min-width: 30px;
  font-size: 16px;
  line-height: 1;
  background: var(--red-50);
  color: var(--red-200);
}

.set-cell {
  width: 30px;
  min-width: 30px;
  font-size: 16px;
  line-height: 1;
  background: var(--grey-35);
  color: var(--grey-300);
}

.finished-cell {
  background: var(--grey-35);
  color: var(--grey-200);
}

.won-set-cell {
  color: var(--grey-900);
}

.active-set-cell {
  background: var(--grey-900);
  color: var(--white);
}

.active-set-cell.scored-flash-team {
  animation: score-flash-team 1s ease-out;
}

.active-set-cell.scored-flash-opposition {
  animation: score-flash-opposition 1s ease-out;
}


.active-set-cell.last-scored-team {
    background: var(--green-500);
    color: var(--white);
}

.active-set-cell.last-scored-opposition {
    background: var(--red-300);
    color: var(--white);
}

@keyframes score-flash-team {
  0% {
    background: var(--white);
    color: var(--green-500);
  }
  100% {
    background: var(--green-500);
    color: var(--white);
  }
}

@keyframes score-flash-opposition {
  0% {
    background: var(--white);
    color: var(--red-300);
  }
  100% {
    background: var(--red-300);
    color: var(--white);
  }
}

.marker {
  color: #f97316;
  font-size: 14px;
  line-height: 1;
}
</style>